import {
  Box,
  chakra,
  Container,
  Flex,
  Link,
  SimpleGrid,
  Stack,
  Text,
  VisuallyHidden,
  useColorModeValue,
} from "@chakra-ui/react";

import { FaInstagram, FaYoutube, FaLinkedin } from "react-icons/fa";
import { useRouter } from "next/router";
import Image from "next/image";
import Imagelog from "../public/logo.png";

const SocialButton = ({ children, label, href, color }) => {
  return (
    <chakra.button
      bg={useColorModeValue("blackAlpha.100", "whiteAlpha.100")}
      rounded={"full"}
      w={8}
      h={8}
      cursor={"pointer"}
      as={"a"}
      href={href}
      display={"inline-flex"}
      alignItems={"center"}
      justifyContent={"center"}
      transition={"background 0.3s ease"}
      _hover={{
        bg: useColorModeValue("blackAlpha.200", "whiteAlpha.200"),
      }}
    >
      <VisuallyHidden>{label}</VisuallyHidden>
      <Box color={color}>{children}</Box>
    </chakra.button>
  );
};

const ListHeader = ({ children }) => {
  return (
    <Text fontWeight={"500"} fontSize={"lg"} mb={2}>
      {children}
    </Text>
  );
};

export default function LargeWithNewsletter() {
  const router = useRouter();

  const handleSectionClick = (sectionId) => (e) => {
    e.preventDefault();
    
    // Check if we're on the home page
    if (router.pathname === '/') {
      // If on home page, scroll to the section
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      // If on different page, navigate to home page with hash
      router.push(`/#${sectionId}`);
    }
  };

  const handleTestimonialsClick = handleSectionClick('testimonials-section');
  const handlePricingClick = handleSectionClick('aptofy-offerings-section');
  const muted = useColorModeValue("gray.500", "gray.400");
  const iconBg = useColorModeValue("#4F46E5", "#6366F1");

  return (
    <Box
      bg={useColorModeValue("gray.50", "gray.900")}
      color={useColorModeValue("gray.700", "gray.200")}
    >
      <Container as={Stack} maxW={"6xl"} py={12}>
        <SimpleGrid
          columns={{ base: 1, sm: 2, lg: 4 }}
          spacing={{ base: 10, lg: 8 }}
          alignItems="start"
        >
          <Stack spacing={4}>
            <Box lineHeight={0}>
              <Image src={Imagelog} width={150} height={48} style={{ width: "150px", height: "auto" }} />
            </Box>
            <Text fontSize={"sm"} color={muted} maxW="240px">
              © 2023-2026 Unlimiteye solutions pvt. ltd. All rights reserved
            </Text>
            <Stack direction={"row"} spacing={3}>
              <SocialButton
                label={"LinkedIn"}
                href={"https://www.linkedin.com/company/aptofy"}
                color={"#0A66C2"}
              >
                <FaLinkedin />
              </SocialButton>
              <SocialButton
                label={"YouTube"}
                href={"https://www.youtube.com/@Aptofy_You"}
                color={"#FF0000"}
              >
                <FaYoutube />
              </SocialButton>
              <SocialButton
                label={"LinkedIn"}
                href={"https://www.linkedin.com/company/unlimiteye"}
                color={"#0A66C2"}
              >
                <FaLinkedin />
              </SocialButton>
            </Stack>
          </Stack>
          <Stack align={"flex-start"} spacing={3}>
            <ListHeader>Featured on</ListHeader>
            <Link
              href="https://tatechfinder.com/vendor/aptofy?ref=badge"
              isExternal
              _hover={{ textDecoration: "none", opacity: 0.85 }}
              aria-label="Featured on TA Tech Finder, Aptofy"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="lg"
                  bg={iconBg}
                  color="white"
                  align="center"
                  justify="center"
                  fontWeight="800"
                  fontSize="sm"
                  letterSpacing="-0.04em"
                  flexShrink={0}
                  aria-hidden
                >
                  TA
                </Flex>
                <Stack spacing={0}>
                  <Text fontSize="sm" fontWeight="700" lineHeight="short">
                    TA Tech Finder
                  </Text>
                  <Text fontSize="xs" color={muted}>
                    Verified listing
                  </Text>
                </Stack>
              </Flex>
            </Link>
          </Stack>
          <Stack align={"flex-start"} spacing={2}>
            <ListHeader>Company</ListHeader>
            <Link href={"/AboutUs"}>About us</Link>
            <Link href={"/blog"}>Blog</Link>
            <Link href={"/contactPage"}>Contact us</Link>
            <Link 
              href="#aptofy-offerings-section" 
              onClick={handlePricingClick}
              cursor="pointer"
            >
              Pricing
            </Link>
            <Link 
              href="#testimonials-section" 
              onClick={handleTestimonialsClick}
              cursor="pointer"
            >
              Testimonials
            </Link>
          </Stack>
          <Stack align={"flex-start"} spacing={2}>
            <ListHeader>Support</ListHeader>
            <Link href={"/Faq"}>Help Center</Link>
            <Link href={"/TermsofService"}>Terms of Service</Link>
            <Link href={"/RefundPolicy"}>Refund Policy</Link>
            <Link href={"/PrivacyPolicy"}>Privacy Policy</Link>
          </Stack>
        </SimpleGrid>
      </Container>
    </Box>
  );
}
